import { Injectable } from '@angular/core';
import { ChatMessage } from '../models/chat-message.model';
import { Observable, ReplaySubject, take } from 'rxjs';
import { MQTTSubscribeListenerService } from '@nida-web/api-mqtt-wrapper';
import { SessionManagerService } from '@nida-web/api/rest/authentication';
import { MqttConnectionState } from '@meddv/ngx-mqtt';
import { UserServable } from '@nida-web/api/generic-interfaces/user-management';
import { ChatMessageFromEnum } from '../models/chat-message-from.enum';

@Injectable({
  providedIn: 'root',
})
export class ChatService {
  private static maxNumberOfRetainedMessages = -1;
  private chatObserver = new ReplaySubject<ChatMessage[]>(1);
  private connectionObserver = new ReplaySubject<boolean>(1);
  private mqttTopic: string;
  private userId: number;
  private userRLName: string;
  private chatMessages: ChatMessage[] = [];

  constructor(
    private mqttService: MQTTSubscribeListenerService,
    private sessionManagerService: SessionManagerService,
    private userService: UserServable
  ) {
    this.sessionManagerService
      .getSessionInformation()
      .pipe(take(1))
      .subscribe((sessionInformation) => {
        this.mqttTopic = `/local-chat`;
        if (sessionInformation.loggedIn) {
          if (sessionInformation.userId !== undefined) {
            this.userId = sessionInformation.userId;
            this.userService
              .getUser(sessionInformation.userId)
              .pipe(take(1))
              .subscribe((user) => {
                this.userRLName = [user.firstName, user.lastName].join(' ');

                this.mqttService.getConnectionStatusObservable().subscribe((status) => {
                  this.connectionObserver.next(status === MqttConnectionState.CONNECTED);
                  if (status === MqttConnectionState.CONNECTED) {
                    this.mqttService.registerTopic(this.mqttTopic).subscribe((message) => {
                      if (message && typeof message === 'string') {
                        try {
                          let chatMessages = JSON.parse(message) as ChatMessage[];

                          if (this.chatMessages.length === 0) {
                            chatMessages = chatMessages.map((chatMessage: ChatMessage) => ({
                              ...chatMessage,
                              transmissionTime: new Date(chatMessage.transmissionTime),
                              from: chatMessage.senderId === this.userId ? ChatMessageFromEnum.ME : ChatMessageFromEnum.THEM,
                            }));

                            this.chatMessages = chatMessages;
                          } else {
                            const lastMessage = chatMessages.slice(-1)[0];
                            lastMessage.transmissionTime = new Date(lastMessage.transmissionTime);
                            lastMessage.from = lastMessage.senderId === this.userId ? ChatMessageFromEnum.ME : ChatMessageFromEnum.THEM;
                            this.chatMessages.push(lastMessage);
                          }

                          this.chatObserver.next(this.chatMessages);
                        } catch (error) {
                          // do nothing, ignore message if the string is no JSON string
                        }
                      }
                    });
                  }
                });
              });
          }
        }
      });
  }

  public static setMaxNumberOfRetainedMessages(max: number) {
    ChatService.maxNumberOfRetainedMessages = max;
  }

  public static hasChat() {
    return ChatService.maxNumberOfRetainedMessages > 0;
  }

  public sendChatMessage(message: string) {
    const chatMessages = [
      ...this.chatMessages,
      {
        message,
        transmissionTime: new Date(),
        senderName: this.userRLName,
        senderId: this.userId,
      },
    ].slice(-1 * ChatService.maxNumberOfRetainedMessages);

    this.mqttService.publishMessageOnTopic(this.mqttTopic, JSON.stringify(chatMessages), true);
  }

  public getChatObservable(): Observable<ChatMessage[]> {
    return this.chatObserver;
  }

  public getConnectionObservable(): Observable<boolean> {
    return this.connectionObserver;
  }
}
